<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store.js';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

// saveHandler is a prop rather than an emit so the dialog can wait for the
// round trip: a rejected write must leave the operator's text on screen.
const props = defineProps({
  saveHandler: { type: Function, required: true },
});

const emit = defineEmits(['delete']);

const { t } = useI18n();

const faqs = useMapGetter('metaAgent/getFaqs');
const uiFlags = useMapGetter('metaAgent/getUIFlags');

const dialogRef = ref(null);
const editingId = ref(null);
const question = ref('');
const answer = ref('');

const canSave = computed(
  () => question.value.trim().length > 0 && answer.value.trim().length > 0
);
const dialogTitle = computed(() =>
  editingId.value
    ? t('META_AGENT.KNOWLEDGE.FAQS.EDIT_TITLE')
    : t('META_AGENT.KNOWLEDGE.FAQS.ADD')
);

const openDialog = faq => {
  editingId.value = faq?.id || null;
  question.value = faq?.question || '';
  answer.value = faq?.answer || '';
  dialogRef.value.open();
};

const save = async () => {
  const saved = await props.saveHandler({
    faqId: editingId.value,
    faq: { question: question.value, answer: answer.value },
  });
  if (saved) dialogRef.value.close();
};

const remove = faq => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('META_AGENT.KNOWLEDGE.FAQS.DELETE_CONFIRM'))) return;
  emit('delete', faq.id);
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-start justify-between gap-4">
      <span class="text-sm text-n-slate-11">
        {{ t('META_AGENT.KNOWLEDGE.FAQS.DESCRIPTION') }}
      </span>
      <Button
        :label="t('META_AGENT.KNOWLEDGE.FAQS.ADD')"
        size="sm"
        icon="i-lucide-plus"
        @click="openDialog(null)"
      />
    </div>

    <div
      v-for="faq in faqs"
      :key="faq.id"
      class="flex items-start justify-between gap-4 p-4 border rounded-xl bg-n-solid-1 border-n-weak"
    >
      <div class="flex flex-col gap-1 min-w-0">
        <span class="text-sm font-medium break-words text-n-slate-12">
          {{ faq.question }}
        </span>
        <span class="text-sm break-words text-n-slate-11">
          {{ faq.answer }}
        </span>
      </div>
      <div class="flex items-center flex-shrink-0 gap-1">
        <Button
          :label="t('META_AGENT.KNOWLEDGE.FAQS.EDIT')"
          size="xs"
          variant="faded"
          color="slate"
          @click="openDialog(faq)"
        />
        <Button
          :label="t('META_AGENT.KNOWLEDGE.FAQS.DELETE')"
          size="xs"
          variant="ghost"
          color="ruby"
          @click="remove(faq)"
        />
      </div>
    </div>

    <span v-if="!faqs.length" class="text-sm text-n-slate-10">
      {{ t('META_AGENT.KNOWLEDGE.FAQS.EMPTY') }}
    </span>

    <Dialog
      ref="dialogRef"
      :title="dialogTitle"
      :confirm-button-label="t('META_AGENT.KNOWLEDGE.FAQS.SAVE')"
      :disable-confirm-button="!canSave"
      :is-loading="uiFlags.savingKnowledge"
      @confirm="save"
    >
      <div class="flex flex-col gap-4">
        <Input
          v-model="question"
          :label="t('META_AGENT.KNOWLEDGE.FAQS.QUESTION')"
          :placeholder="t('META_AGENT.KNOWLEDGE.FAQS.QUESTION_PLACEHOLDER')"
        />
        <TextArea
          v-model="answer"
          :label="t('META_AGENT.KNOWLEDGE.FAQS.ANSWER')"
          :placeholder="t('META_AGENT.KNOWLEDGE.FAQS.ANSWER_PLACEHOLDER')"
          :max-length="2000"
          auto-height
        />
      </div>
    </Dialog>
  </div>
</template>
